import { YearbookElement, CornerRadii } from '@/types/yearbook';

/**
 * Normalize raw API elements: convert snake_case properties to camelCase.
 * This handles elements loaded from the backend that may use snake_case keys
 * (e.g. corner_radii, image_url, etc.) instead of the camelCase expected by the frontend.
 */
export function normalizeElements(elements: any[]): YearbookElement[] {
  return elements.map(normalizeElement);
}

function normalizeElement(el: any): YearbookElement {
  const result = { ...el };

  // corner_radii → cornerRadii
  if (el.corner_radii && !el.cornerRadii) {
    const raw = el.corner_radii;
    result.cornerRadii = (typeof raw === 'string' ? JSON.parse(raw) : raw) as CornerRadii;
    delete result.corner_radii;
  }
  // Also handle cornerRadii that might be a string
  if (result.cornerRadii && typeof result.cornerRadii === 'string') {
    try { result.cornerRadii = JSON.parse(result.cornerRadii) as CornerRadii; } catch { delete result.cornerRadii; }
  }

  // image_url → imageUrl
  if (el.image_url !== undefined && el.imageUrl === undefined) {
    result.imageUrl = el.image_url;
    delete result.image_url;
  }

  // image_ratio → imageRatio
  if (el.image_ratio !== undefined && el.imageRatio === undefined) {
    result.imageRatio = el.image_ratio;
    delete result.image_ratio;
  }

  // image_offset → imageOffset
  if (el.image_offset !== undefined && el.imageOffset === undefined) {
    result.imageOffset = el.image_offset;
    delete result.image_offset;
  }

  // image_scale → imageScale
  if (el.image_scale !== undefined && el.imageScale === undefined) {
    result.imageScale = el.image_scale;
    delete result.image_scale;
  }

  // from_layout → fromLayout
  if (el.from_layout !== undefined && el.fromLayout === undefined) {
    result.fromLayout = el.from_layout;
    delete result.from_layout;
  }

  // user_added → userAdded
  if (el.user_added !== undefined && el.userAdded === undefined) {
    result.userAdded = el.user_added === true || el.user_added === 'true';
    delete result.user_added;
  }

  // user_id → userId (accepts string ids from the API)
  if (el.user_id !== undefined && el.user_id !== null) {
    if (result.userId === undefined || result.userId === null) {
      const val = typeof el.user_id === 'string' ? parseInt(el.user_id, 10) : el.user_id;
      result.userId = typeof val === 'number' && !isNaN(val) ? val : el.user_id;
    }
    delete result.user_id;
  }
  if (typeof result.userId === 'string') {
    const val = parseInt(result.userId, 10);
    if (!isNaN(val)) result.userId = val;
  }

  // picture_id → pictureId
  if (el.picture_id !== undefined && el.pictureId === undefined) {
    result.pictureId = el.picture_id;
    delete result.picture_id;
  }

  // is_circle → isCircle
  if (el.is_circle !== undefined && el.isCircle === undefined) {
    result.isCircle = el.is_circle === 'true' || el.is_circle === true;
    delete result.is_circle;
  }

  // is_sticker → isSticker
  if (el.is_sticker !== undefined && el.isSticker === undefined) {
    result.isSticker = el.is_sticker === 'true' || el.is_sticker === true;
    delete result.is_sticker;
  }

  // z_priority → zPriority
  if (el.z_priority !== undefined && el.zPriority === undefined) {
    result.zPriority = el.z_priority;
    delete result.z_priority;
  }

  // text_style → textStyle
  if (el.text_style !== undefined && el.textStyle === undefined) {
    result.textStyle = el.text_style;
    delete result.text_style;
  }

  // shape_type → shapeType
  if (el.shape_type !== undefined && el.shapeType === undefined) {
    result.shapeType = el.shape_type;
    delete result.shape_type;
  }

  // preview_image_url → previewImageUrl
  if (el.preview_image_url !== undefined && el.previewImageUrl === undefined) {
    result.previewImageUrl = el.preview_image_url;
    delete result.preview_image_url;
  }

  // background_color → backgroundColor
  if (el.background_color !== undefined && el.backgroundColor === undefined) {
    result.backgroundColor = el.background_color;
    delete result.background_color;
  }

  // border_radius → borderRadius
  if (el.border_radius !== undefined && el.borderRadius === undefined) {
    result.borderRadius = el.border_radius;
    delete result.border_radius;
  }

  // stroke_width → strokeWidth
  if (el.stroke_width !== undefined && el.strokeWidth === undefined) {
    result.strokeWidth = el.stroke_width;
    delete result.stroke_width;
  }

  // force_dimension → forceDimension
  if (el.force_dimension !== undefined && el.forceDimension === undefined) {
    result.forceDimension = el.force_dimension === true || el.force_dimension === 'true';
    delete result.force_dimension;
  }

  // qrcode_id → qrcodeId
  if (el.qrcode_id !== undefined && el.qrcodeId === undefined) {
    result.qrcodeId = el.qrcode_id;
    delete result.qrcode_id;
  }

  // z_index → zIndex (integer)
  if (el.z_index !== undefined && el.zIndex === undefined) {
    const val = typeof el.z_index === 'string' ? parseInt(el.z_index, 10) : el.z_index;
    result.zIndex = isNaN(val) ? undefined : val;
    delete result.z_index;
  }
  if (result.zIndex !== undefined && typeof result.zIndex === 'string') {
    const val = parseInt(result.zIndex, 10);
    result.zIndex = isNaN(val) ? undefined : val;
  }

  // group_id → groupId
  if (el.group_id !== undefined && el.groupId === undefined) {
    result.groupId = el.group_id;
    delete result.group_id;
  }

  // sticker_format → stickerFormat
  if (el.sticker_format !== undefined && el.stickerFormat === undefined) {
    result.stickerFormat = el.sticker_format;
    delete result.sticker_format;
  }

  // sticker_color → stickerColor
  if (el.sticker_color !== undefined && el.stickerColor === undefined) {
    result.stickerColor = el.sticker_color;
    delete result.sticker_color;
  }

  // forbidden_words → forbiddenWords
  if (el.forbidden_words !== undefined && el.forbiddenWords === undefined) {
    result.forbiddenWords = Array.isArray(el.forbidden_words) ? el.forbidden_words : [];
    delete result.forbidden_words;
  }

  return result as YearbookElement;
}
